// TODAY orb (the day) — the five ORDER rows. Spec: docs/redesign/ORB_SPEC.md.
import React from 'react';
import { View } from 'react-native';
import { DOW, addDaysKey, clock, clockLong, dateKeyOf, keyToDate, trunc } from './time';
import { Btn, Head, Line, Mark, PromptLine, Quiet, Txt } from './ui';
import { NOT_SET, ObjectiveWidget, aimsOf, chainOf, eventsFor, goalName, joinLine, slotState } from './shared';
import { itemsForSector } from './data';
import type { OrbItem, RowCtx, RowSet } from './types';

/** The day's objective: the user's correction, else the top open task (ORACLE / KYRS pre-fill it). */
export function todayObjective(c: Pick<RowCtx, 'data' | 'notes' | 'sector'>, dayKey: string): string | null {
  const day = itemsForSector(c.data.items, c.sector).filter((i) => i.date === dayKey);
  const open = day.filter((i) => !i.completed);
  return c.notes.get(`T:${dayKey}:${c.sector ?? '-'}`)?.text || (open[0] ?? day[0])?.title || null;
}

export function todayRows(c: RowCtx, dayKey: string): RowSet {
  const { data, now, sector, spine, acts, notes } = c;
  const todayKey = dateKeyOf(now);
  const isToday = dayKey === todayKey;
  const node = sector && spine ? spine[sector] : null;
  const aim = aimsOf(node)[0]?.label ?? node?.endeavour ?? null;
  const day = itemsForSector(data.items, sector).filter((i) => i.date === dayKey);
  const open = day.filter((i) => !i.completed);
  const doneN = day.length - open.length;
  const tomorrow = addDaysKey(dayKey, 1);

  // ---- O · objective ------------------------------------------------------
  const noteKey = `T:${dayKey}:${sector ?? '-'}`;
  const note = notes.get(noteKey);
  const objective = todayObjective(c, dayKey);
  const overdue = isToday ? open.filter((i) => i.start && i.start < now).length : 0;
  const pace = isToday && day.length ? (overdue > 0 ? 'behind' : 'on pace') : null;
  const oLine = objective ? joinLine([objective, aim && `serves ${aim}`, pace]) : 'Nothing planned · set the day in KYRS';

  // ---- R · ready ----------------------------------------------------------
  const slots = day.map((i) => ({ i, s: slotState(i, eventsFor(c, dayKey)) }));
  const ready = slots.filter((x) => x.s.ready).length;
  const block = slots.find((x) => !x.s.ready);
  const rLine = day.length ? joinLine([`${ready} of ${day.length} tasks ready`, block && `${trunc(block.i.title, 16)}: ${block.s.why}`]) : 'No tasks yet · pick them in KYRS';
  const rShow = [...slots.filter((x) => !x.s.ready), ...slots.filter((x) => x.s.ready)].slice(0, 4);

  // ---- D · order ----------------------------------------------------------
  const label = (i: OrbItem) => (i.start ? `${clock(i.start)} ${trunc(i.title, 14)}` : trunc(i.title, 16));
  const dLine = day.length ? joinLine([...day.slice(0, 2).map(label), day.length > 2 && `(+${day.length - 2})`]) : 'No order yet · plan the day in KYRS';

  // ---- E · now ------------------------------------------------------------
  const inWindow = open.find((i) => i.start && i.start <= now && now < new Date(i.start.getTime() + i.minutes * 60000));
  const current = (isToday ? inWindow : null) ?? open[0] ?? null;
  const nextUp = current ? open.find((i) => i !== current) ?? null : null;
  let left = '';
  if (current) {
    if (current.start && isToday) {
      const end = new Date(current.start.getTime() + current.minutes * 60000);
      left = now < current.start ? `starts ${clock(current.start)}` : now < end ? `${Math.max(1, Math.ceil((end.getTime() - now.getTime()) / 60000))} min left` : 'past its slot';
    } else left = `${current.minutes} min`;
  }
  const eLine = current ? joinLine([trunc(current.title, 20), left, `${doneN} of ${day.length} done`]) : day.length ? `All done · ${doneN} of ${day.length}` : 'Nothing planned today';

  // ---- R · close ----------------------------------------------------------
  const closeAt = keyToDate(dayKey);
  const [eh, em] = data.game.eveningSessionTime.split(':').map(Number);
  closeAt.setHours(eh, em, 0, 0);
  const after = now >= closeAt;
  const lesson = notes.get(`L:day:${dayKey}`)?.text;
  const fLine = after ? joinLine([`${doneN} of ${day.length} done`, lesson ?? 'add the lesson']) : `Close at ${clockLong(closeAt)}`;

  // ---- full answers (read top to bottom) ----------------------------------
  const dayName = isToday ? 'today' : DOW[keyToDate(dayKey).getDay()];
  const chain = chainOf([['Aim', aim], ['Goal', goalName(sector, node)], ['Endeavour', node?.endeavour]]);
  const oAnswer = [
    `Objective: ${objective ?? NOT_SET}`,
    `Supports: ${chain ?? NOT_SET}`,
    `Done when: ${day.length ? `all ${day.length} planned tasks are done (${doneN} so far)` : NOT_SET}${pace ? ` · ${pace}` : ''}`,
  ];
  const blocks = slots.filter((x) => !x.s.ready);
  const rAnswer = day.length
    ? [blocks.length ? `NOT READY — ${ready} of ${day.length} tasks ready` : `READY — all ${day.length} tasks have a clear slot`, ...blocks.slice(0, 3).map((x) => `✗ ${x.i.title}: ${x.s.why}`), ...(blocks.length > 3 ? [`+${blocks.length - 3} more not ready`] : [])]
    : [`Readiness: ${NOT_SET} — no tasks planned`];
  const dAnswer = day.length
    ? [...day.slice(0, 4).map((i, n) => `${n + 1}. ${i.start ? `${clock(i.start)} ` : ''}${i.title}${i.completed ? ' ✓' : ''}`), ...(day.length > 4 ? [`+${day.length - 4} more`] : [])]
    : [`Plan: ${NOT_SET} — no tasks in order`];
  const eAnswer = current
    ? [`Now: ${current.title}`, `Time: ${left}`, `Progress: ${doneN} of ${day.length} done`, ...(nextUp ? [`Next up: ${nextUp.start ? `${clock(nextUp.start)} ` : ''}${nextUp.title}`] : [])]
    : [day.length ? `Everything planned is done (${doneN} of ${day.length})` : `Now: ${NOT_SET} — nothing planned`];
  const carry = open.map((i) => i.title);
  const fAnswer = [
    `Outcome: ${day.length ? `${doneN} of ${day.length} done` : 'nothing was planned'}${after ? '' : ` · closes ${clockLong(closeAt)}`}`,
    `Carries forward: ${carry.length ? carry.slice(0, 3).join(', ') + (carry.length > 3 ? ` (+${carry.length - 3})` : '') : 'nothing'}`,
    `Change: ${lesson ?? NOT_SET}`,
  ];

  return {
    orient: {
      question: `Why ${dayName}, and what counts as done?`,
      line: oLine,
      answer: oAnswer,
      tone: objective ? undefined : 'quiet',
      swipe: { kind: 'tool', tool: 'atlas', label: 'ATLAS' },
      widget: (
        <ObjectiveWidget
          key={noteKey}
          text={objective}
          serves={aim ? `${aim}${pace ? ` · ${pace}` : ''}` : pace}
          state={note?.state}
          empty="Nothing is planned for this day yet."
          onAccept={() => notes.set(noteKey, { ...note, state: 'accepted' })}
          onCorrect={(text) => notes.set(noteKey, { text, state: 'corrected' })}
          onChange={() => acts.go({ kind: 'tool', tool: 'atlas', label: 'ATLAS' })}
        />
      ),
    },
    regulate: {
      question: `Do I have everything ${dayName} needs?`,
      line: rLine,
      answer: rAnswer,
      tone: block ? 'warn' : day.length ? 'ok' : 'quiet',
      swipe: { kind: 'tool', tool: 'krnos', view: 'daily', label: 'KRNOS day' },
      widget: (
        <View style={{ alignSelf: 'stretch', gap: 3 }}>
          {rShow.length ? (
            rShow.map(({ i, s }) => (
              <Line key={i.key} right={!s.ready ? <Btn label="Move" disabled={acts.busy} onPress={() => void acts.move(i, tomorrow)} /> : undefined}>
                <Mark ok={s.ready} />
                <Txt lines={1} style={{ flex: 1 }}>
                  {i.start ? `${clock(i.start)} ` : ''}
                  {i.title}
                  {!s.ready && s.why ? ` — ${s.why}` : ''}
                </Txt>
              </Line>
            ))
          ) : (
            <Quiet>No tasks planned for this day yet.</Quiet>
          )}
          {day.length > rShow.length ? <Txt faint style={{ textAlign: 'center' }}>+{day.length - rShow.length} more</Txt> : null}
        </View>
      ),
    },
    direct: {
      question: 'What’s the plan for the day?',
      line: dLine,
      answer: dAnswer,
      tone: day.length ? undefined : 'quiet',
      swipe: { kind: 'tool', tool: 'metis', label: 'METIS' },
      widget: (
        <View style={{ alignSelf: 'stretch', gap: 3 }}>
          {day.length ? (
            day.slice(0, 5).map((i, idx) => {
              const up = day[idx - 1];
              const dn = day[idx + 1];
              return (
                <Line
                  key={i.key}
                  right={
                    <View style={{ flexDirection: 'row', gap: 3 }}>
                      <Btn label="▲" disabled={!up || acts.busy} onPress={() => up && void acts.swap(i, up, 'slot')} />
                      <Btn label="▼" disabled={!dn || acts.busy} onPress={() => dn && void acts.swap(i, dn, 'slot')} />
                    </View>
                  }
                >
                  <Txt faint w={46}>{i.start ? clock(i.start) : '—'}</Txt>
                  <Txt lines={1} style={{ flex: 1, textDecorationLine: i.completed ? 'line-through' : 'none' }}>{i.title}</Txt>
                </Line>
              );
            })
          ) : (
            <Quiet>No order yet — KYRS picks the day’s tasks.</Quiet>
          )}
          {day.length > 5 ? <Txt faint style={{ textAlign: 'center' }}>+{day.length - 5} more</Txt> : null}
        </View>
      ),
    },
    execute: {
      question: 'What am I doing now?',
      line: eLine,
      answer: eAnswer,
      tone: current ? undefined : 'quiet',
      swipe: current ? { kind: 'drill', level: 'S', stepKey: current.key, tool: 'metis', label: 'its Step orb' } : { kind: 'tool', tool: 'metis', label: 'METIS' },
      widget: current ? (
        <View style={{ alignSelf: 'stretch', gap: 6, alignItems: 'center' }}>
          <Head>{current.title}</Head>
          <Txt soft style={{ textAlign: 'center' }}>
            {left} · {doneN} of {day.length} done
          </Txt>
          <View style={{ flexDirection: 'row', gap: 6 }}>
            <Btn label="Done" tone="gold" disabled={acts.busy} onPress={() => void acts.done(current)} />
            <Btn label="Open Step" onPress={() => acts.drillStep(current)} />
          </View>
          {nextUp ? (
            <Txt faint lines={1} style={{ textAlign: 'center' }}>
              next up: {nextUp.start ? `${clock(nextUp.start)} ` : ''}
              {nextUp.title}
            </Txt>
          ) : null}
        </View>
      ) : (
        <Quiet>{day.length ? 'Everything planned is done.' : 'Nothing is planned for this day. Add tasks in KYRS.'}</Quiet>
      ),
    },
    revise: {
      question: 'What happened, and what needs to change?',
      line: fLine,
      answer: fAnswer,
      tone: 'quiet',
      swipe: { kind: 'session', session: 'night', tool: 'clio', label: 'CLIO evening session' },
      widget: (
        <View style={{ alignSelf: 'stretch', gap: 4 }}>
          {day.length ? (
            day.slice(0, 3).map((i) => (
              <Line
                key={i.key}
                right={
                  <View style={{ flexDirection: 'row', gap: 3 }}>
                    <Btn label="Done" tone={i.completed ? 'ok' : 'ghost'} disabled={acts.busy} onPress={() => void acts.done(i)} />
                    <Btn label="Start" tone={!i.completed && i.missed === 'Started' ? 'warn' : 'ghost'} disabled={acts.busy} onPress={() => void acts.missed(i, 'Started')} />
                    <Btn label="Move" disabled={acts.busy} onPress={() => void acts.move(i, tomorrow)} />
                  </View>
                }
              >
                <Txt lines={1} style={{ flex: 1 }}>{i.title}</Txt>
              </Line>
            ))
          ) : (
            <Quiet>Nothing was planned for this day.</Quiet>
          )}
          {day.length > 3 ? <Txt faint style={{ textAlign: 'center' }}>+{day.length - 3} more</Txt> : null}
          <PromptLine prompt="What did today teach?" saved={lesson} onSave={(t) => acts.lesson('day', dayKey, t)} />
        </View>
      ),
    },
  };
}
